import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import SearchIcon from '@material-ui/icons/Search';
import InputBase from '@material-ui/core/InputBase';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import DatePicker from 'react-datepicker';
import LinearProgress from '@material-ui/core/LinearProgress';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Auth from '../services/auth';
import { alpha, makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { getDateOffset } from '../services/accountSettings';
import _ from 'lodash';
import UserDataAccess from '../services/userDataAccess';
import ScheduleDataAccess from '../services/scheduleDataAccess';

const auth = new Auth();
const searchAccess = new ScheduleDataAccess();
const userDataAccess = new UserDataAccess();

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(2),
        color: theme.palette.text.secondary,
    },
    paperReports: {
        padding: theme.spacing(2),
        color: theme.palette.text.secondary,
        height: '100%',
        maxHeight: 324,
        overflow: 'auto'
    },
    search: {
        position: 'relative',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: alpha(theme.palette.common.white, 0.15),
        '&:hover': {
            backgroundColor: alpha(theme.palette.common.white, 0.25),
        },
        marginRight: theme.spacing(2),
        marginLeft: 0,
        width: '100%',
        [theme.breakpoints.up('sm')]: {
            marginLeft: theme.spacing(3),
            width: 'auto',
        },
    },
    searchIcon: {
        padding: theme.spacing(0, 2),
        height: '100%',
        position: 'absolute',
        pointerEvents: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    inputRoot: {
        color: 'inherit',
    },
    inputInput: {
        padding: theme.spacing(1, 1, 1, 0),
        paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
        transition: theme.transitions.create('width'),
        width: '100%',
        [theme.breakpoints.up('md')]: {
            width: '20ch',
        },
    },
    searchInput: {
        "& input::placeholder": {
            color: 'black',
            fontWeight: 'bold',
            opactiy: 1
        },
    },
    textField: {
        maxWidth: 155,
    },
    dateWrapper: {
        display: 'inline-block'
    },
    dateContainer: {
        paddingBottom: '4px',
    },
    root: {
        width: '100%',
        height: '500px'
    },
    button: {
        marginRight: theme.spacing(1),
    },
    instructions: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    container: {
        width: '100%'
    }
}));

const defaultFromDate = {
    date: moment().subtract(30, 'days'),
    input: moment().subtract(30, 'days').format("MM/DD/YYYY")
};

const defaultToDate = {
    date: moment(),
    input: moment().format("MM/DD/YYYY")
};

let currentDate = new Date();

const PatientSearch = ({ onPatientClick }) => {
    const classes = useStyles();
    const [fromDate, setFromDate] = useState(defaultFromDate);
    const [toDate, setToDate] = useState(defaultToDate);
    const [loading, setLoading] = useState(false);
    const [searchResults, setSearchResult] = useState([]);
    const [searchText, setSearchText] = useState();
    const [ScheduleDetailDialogSettings, setScheduleDetailDialogSettings] = useState({ account: null, id: null, open: false });

    useEffect(() => {
        if (searchText) {
            debounceSearch(searchText);
        }
    }, [searchText, toDate, fromDate])

    const searchSchedule = (searchText) => {
        const searchRequest = {
            FromDate: fromDate.input,
            ToDate: toDate.input,
            Account: auth.getActiveAccount(),
            DictatorId: "",
            IncludeFilteredProcedures: false,
            StatusFilters: [
                "NeedsDictation", "New", "InProgress", "CompletedWithQuestions", "Completed","Signed"
            ]
        }
        setLoading(true);
        searchAccess.search(searchRequest)
            .then(result => {
                setSearchResult(filterResult(searchText, result));
            })
            .catch(error => { })
            .finally(() => setLoading(false));
    }
    const debounceSearch = _.debounce((value) => searchSchedule(value), 500)

    const filterResult = (searchText, result) => {
        if (!searchText) return [];
        const lowerSearchText = searchText.toLowerCase();
        return result.filter(x => x.PatientFirstName.toLowerCase().includes(lowerSearchText) || x.PatientLastName.toLowerCase().includes(lowerSearchText))
    }

    const handlePatientClick = (selectedPatient) => {
        setSearchResult([]);
        onPatientClick(selectedPatient);
    }

    const onListItemClick = (account, id) => {
        setScheduleDetailDialogSettings({ account, id, open: true });
    }

    const handleFromDateChange = (date) => {
        if (moment(date).isValid()) {
            setFromDate({ date: moment(date), input: moment(date).format("MM/DD/YYYY") });
        }
    }

    const handleToDateChange = (date) => {
        if (moment(date).isValid()) {
            setToDate({ date: moment(date), input: moment(date).format("MM/DD/YYYY") });
        }
    }

    const handleSearchReset = () => {
        setFromDate(defaultFromDate);
        setToDate(defaultToDate);
        searchSchedule(null);
        setSearchText("");
    }

    const onSearchChange = (event) => {
        const { value } = event.target;
        setSearchText(value);
    }

    const $el = document.querySelector('body');
    const PopperContainer = ({ children }) => (
        ReactDOM.createPortal(
            children,
            $el,
        )
    );
    return (
        <div className={classes.container}>
            <Grid container className={classes.dateContainer} spacing={2}>
                <Grid item>
                    <div className={classes.dateWrapper}>
                        <DatePicker
                            disabledKeyboardNavigation
                            selected={fromDate.date}
                            onChange={handleFromDateChange}
                            customInput={
                                <div>
                                    <TextField
                                        className={classes.textField}
                                        id="fromDate"
                                        label="From Date of Service"
                                        value={fromDate.input}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </div>
                            }
                        />
                    </div>
                </Grid>
                <Grid item>
                    <div className={classes.dateWrapper}>
                        <DatePicker
                            disabledKeyboardNavigation
                            selected={toDate.date}
                            onChange={handleToDateChange}
                            customInput={
                                <div>
                                    <TextField
                                        className={classes.textField}
                                        id="toDate"
                                        label="To Date of Service"
                                        value={toDate.input}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </div>
                            }
                        />
                    </div>
                </Grid>
                <Grid item>
                    <Button color="primary" variant="contained" onClick={handleSearchReset}>Reset</Button>
                </Grid>
            </Grid>
            <Paper style={{
                display: 'flex'
                , alignItems: 'center'
                , paddingLeft: '8px'
                , paddingRight: '8px'
                , paddingTop: '4px'
                , paddingBottom: '4px'
            }}>
                <InputBase
                    placeholder="Search for patient…"
                    style={{ width: '100%' }}
                    className={classes.searchInput }
                    inputProps={{ 'aria-label': 'search', className: classes.searchInput }}
                    onChange={onSearchChange}
                />
                <div>
                    <SearchIcon />
                </div>
            </Paper>
            {loading ?
                (<LinearProgress />) :
                (
                    searchResults.length > 0 ? (
                        <Paper style={{ maxHeight: 200, overflow: 'auto' }}>
                            <List>
                                {searchResults.map((result, key) => (
                                    <ListItem key={key} button onClick={() => handlePatientClick(result)}>
                                        <ListItemText>
                                            {`${result.CaseId} ${result.PatientFirstName} ${result.PatientLastName} ${result.DateOfService}`}
                                        </ListItemText>
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    ) : null
                )
            }
        </div>
    )
}

export default PatientSearch;


// WEBPACK FOOTER //
// ./src/components/PatientSearch.js